/* eslint-disable */
import { defineStore } from 'pinia';
import _ from 'lodash';
import moment from 'moment';
import { useOddsStore } from './odds';
import { useDepositsStore } from './bank/deposits';
import { useWithdrawalsStore } from './bank/withdrawals';
import { useInitializeStore } from './initialize';
import { useTimezonesStore } from './timezones';
import { usePlatformConfigurationStore } from './platform_configuration';

export const useCustomerStore = defineStore('customer', {
	state: () => ({
		customer: null,
		customer_protection: null,
		protection_limits: {
			login_limit: null,
			time_out: null,
			reality_check: null,
			time_limits: null,
			bet_limits: null,
			cancel_withdraw_request: null,
			loss_limits: null,
			deposit_limits: null,
		},
		protection_limits_errors: {},
		auth_token: null,
		balance_visibility: true,
		customer_notification_id: null,
		redirect_after_login: {},
		platform_notifications: [],
		dismissed_platform_notifications: [],
		customer_unread_notifications: [],
		customer_notifications: [],
	}),
	getters: {
		availableBonuses(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.available_bonus;
		},
		availableFreeBets(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.freebets;
		},
		activeBonuses(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.active_bonus;
		},
		bonusHistory(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.customer_bonus_history;
		},
		freebetsHistory(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.customer_freebets_history;
		},
		freespinHistory(state) {
			if (!state.customer || !state.customer.offers) return;
			return state.customer.offers.customer_freespins_history;
		},
		isLoggedIn(state) {
			if (!state.customer) return false;
			return true;
		},
		getCustomer(state) {
			return state.customer;
		},
		hasBetInfluencerPlan(state) {
			return state.customer?.bet_influencer_plan != null;
		},
		hasVerifiedEmail(state) {
			return state.customer?.has_verified_email ?? false;
		},
		getProtectionLimits(state) {
			return state.protection_limits;
		},
		customerRealityCheckTimestamp(state) {
			if (!state.customer) return null;

			if (!state.protection_limits.reality_check) return null;

			if (!state.protection_limits.reality_check.customer_protection_configuration_choice) return null;

			if (!state.protection_limits.reality_check.customer_protection_configuration_choice.value) return null;

			return moment().add(state.protection_limits.reality_check.customer_protection_configuration_choice.value, state.protection_limits.reality_check.customer_protection_configuration_choice.type).unix();
		},
		customerTimeLimitsStatus(state) {
			if (!state.customer) return null;

			if (!state.protection_limits.time_limits) return null;

			var time_limits = state.protection_limits.time_limits

			var time_limits_array = {
				'type': null,
				'exceed': false,
				'timestamp': null,
			};

			if (time_limits.duration.remaining_time.monthly != null && time_limits.duration.remaining_time.monthly <= 0) {
				time_limits_array.type = 'monthly';
				time_limits_array.exceed = true;

				return time_limits_array;
			}

			if (time_limits.duration.remaining_time.weekly != null && time_limits.duration.remaining_time.weekly <= 0) {
				time_limits_array.type = 'weekly';
				time_limits_array.exceed = true;

				return time_limits_array;
			}

			if (time_limits.duration.remaining_time.daily != null && time_limits.duration.remaining_time.daily <= 0) {
				time_limits_array.type = 'daily';
				time_limits_array.exceed = true;

				return time_limits_array;
			}

			var timestamps_array = {
				daily: time_limits.duration.remaining_time.daily ? moment().add(time_limits.duration.remaining_time.daily, 'minutes').unix() : null,
				weekly: time_limits.duration.remaining_time.weekly ? moment().add(time_limits.duration.remaining_time.weekly, 'minutes').unix() : null,
				monthly: time_limits.duration.remaining_time.monthly ? moment().add(time_limits.duration.remaining_time.monthly, 'minutes').unix() : null,
			};

			// Sort array values
			timestamps_array = _.fromPairs(_.sortBy(_.toPairs(timestamps_array), 1));

			time_limits_array.type = Object.keys(timestamps_array)[0];
			time_limits_array.timestamp = Object.values(timestamps_array)[0];

			return time_limits_array;
		},
		customerProtectionSection: (state) => (slug) => {
			let section = _.find(state.customer_protection, { 'slug': slug });
			return section;
		},
		customerProtectionSectionChoices: (state) => (slug) => {
			let section = _.find(state.customer_protection, {'slug': slug});
			if (!section) return [];

			// Group by type
			const groupByType = _.groupBy(section.choices, 'type');

			// Sort arrays
			for (const group_type in groupByType) {
				if (Object.hasOwnProperty.call(groupByType, group_type)) {
					const element = groupByType[group_type];
					groupByType[group_type] = _.sortBy(element)
				}
			}

			// Default sorting
			let sort_list = [
				'no limit',
				'minutes',
				'hours',
				'days',
				'weeks',
				'months',
				'years',
				'infinite',
				'numeric'
			];

			// Denife array to return
			let choices_sorted = [];

			sort_list.forEach(key => {
				// Push No Limit choice
				if (key == 'no limit') {
					let has_no_limit = _.find(section.choices, { 'type': 'no limit' });
					if (has_no_limit != null) choices_sorted.push(has_no_limit);
				}
				// Push Infinite choice
				else if (key == 'infinite') {
					let has_infinite = _.find(section.choices, { 'type': 'infinite' });
					if (has_infinite != null) choices_sorted.push(has_infinite);
				}

				// Sort and push any other choice
				else if (groupByType[key] && groupByType[key].length > 0) {
					Object.keys(groupByType[key]).forEach(choice => {
						choices_sorted.push(groupByType[key][choice]);
					});
				}
			});
			// Return choices
			if (choices_sorted) return choices_sorted;
		},
		hasCustomerProtectionLimits(state) {
			return state.customer?.has_customer_protection_limits ?? false;
		},
		getProtectionLimitsErrors(state) {
			return state.protection_limits_errors;
		},
		betslipChanges(state) {
			if (!state.isLoggedIn) return 'always_ask';

			return state.getCustomer.betslip_changes;
		},
		freebets(state) {
			if (!state.isLoggedIn) return [];

			return state.getCustomer?.offers?.freebets || [];
		},
		verificationDaysLeft(state) {
			if (!usePlatformConfigurationStore().showVerificationWarning) return null;
			if (!state.customer) return null;
			return state.customer.verification_days_left;
		},
		unclaimed_bonuses_length(state) {
			if (!state.customer || !state.customer.offers) return 0;

			let bonuses_number = Object.keys(state.customer?.offers?.available_bonus || {}).length;
			let freebet_number = Object.keys(state.customer?.offers?.freebets || {}).length;
			let freespin_number = Object.keys(state.customer?.offers?.freespins || {}).length;

			return bonuses_number + freebet_number + freespin_number;
		},
		customerNotificationsUnread(state) {
			if (!state.customer) return [];

			return state.customer.unread_notifications;
		},
		customerLastNotification(state) {
			if (!state.customer && state.customer.last_notification) return [];

			return state.customer.last_notification;
		},
		customerAllPlatformNotifications(state) {
			if (!state.customer) return [];

			return state.platform_notifications.filter(
				notification => !state.dismissed_platform_notifications.includes(notification.id)
			);
		},
		customerGeneralPlatformNotifications(state) {
			if (!state.customer) return [];

			return state.platform_notifications.filter(
				notification => notification.product === 'general' && !state.dismissed_platform_notifications.includes(notification.id)
			);
		},
		customerSportsbookPlatformNotifications(state) {
			if (!state.customer) return [];

			return state.platform_notifications.filter(
				notification => (notification.product === 'sportsbook' || notification.product === 'general') && !state.dismissed_platform_notifications.includes(notification.id)
			);
		},
		customerCasinoPlatformNotifications(state) {
			if (!state.customer) return [];

			return state.platform_notifications.filter(
				notification => (notification.product === 'casino' || notification.product === 'general') && !state.dismissed_platform_notifications.includes(notification.id)
			);
		},
		customerLiveCasinoPlatformNotifications(state) {
			if (!state.customer) return [];

			return state.platform_notifications.filter(
				notification => (notification.product === 'live_casino' || notification.product === 'general') && !state.dismissed_platform_notifications.includes(notification.id)
			);
		},
		getCustomerPopUpNotifications(state) {
			if (!state.customer) return [];

			return this.customer_notifications;
		},
		getNumberOfUnreadDropdownNotification(state) {
			if (!state.customer) return 0;

			return state.customer_notifications.filter(notification => notification.read_at === null).length;
		},
		getUnreadDropdownNotifications(state) {
			if (!state.customer || !state.customer_notifications) return [];

			return state.customer_notifications.filter(notification => notification.read_at === null);
		},
		getDepositFreebetBonus: (state) => (payment_gateway_id) => {
			const deposit_freebets = state.customer?.offers?.deposit_freebets || [];

			return deposit_freebets.filter(bonus =>
				bonus.payment_gateways?.some(gateway => gateway.id === payment_gateway_id)
			);
		},
		getDepositFreespinBonus: (state) => (payment_gateway_id) => {
			const deposit_freespins = state.customer?.offers?.deposit_freespins || [];

			return deposit_freespins.filter(bonus =>
				bonus.payment_gateways?.some(gateway => gateway.id === payment_gateway_id)
			);
		},
		getAllDepositBonuses: (state) => (payment_gateway_id) => {
			const deposit_freebets = state.getDepositFreebetBonus(payment_gateway_id) ?? [];
			const deposit_freespins = state.getDepositFreespinBonus(payment_gateway_id) ?? [];
			let deposit_wagering = useDepositsStore().getAvailableBonuses ?? [];

			if (typeof deposit_wagering === 'object') {
				deposit_wagering = Object.values(deposit_wagering);
			}

			return [...deposit_freebets, ...deposit_freespins, ...deposit_wagering];
		},
		getCustomersUtcOffset(state) {
			const timezonesStore = useTimezonesStore();

			let utc_offset = timezonesStore.selectedTimezone.utc_offset;
			if (!state.isLoggedIn) utc_offset = moment().format('Z');

			return utc_offset;
		}
	},
	actions: {
		fetchCustomer() {
			const initializeStore = useInitializeStore();

			const device = initializeStore.isMobile ? 'mobile' : 'desktop';

			return global.axios.get(process.env.VUE_APP_API_URL + '/me', { params: { device: device, new: true } }).then((response) => {
				const oddsStore = useOddsStore();
				oddsStore.odds_preview = response.data.data.customer.odds_format;

				this.setCustomer(response.data.data.customer);
				this.customer.favourites = { markets: [], fixtures: [], slots: [], tables: [], betslips: [] };
				this.platform_notifications = response.data.data.platform_notifications;
				return Promise.resolve(response.data.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			});
		},
		fetchBalance() {
			// Do not attempt to fetch if not logged in
			if (!this.isLoggedIn) return;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/balance').then((response) => {
				this.customer.balance = response.data.data;
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		emptyCustomer() {
			this.customer = null;
			this.customer_protection = null;
			this.protection_limits = { login_limit: null, time_out: null, reality_check: null, time_limits: null, bet_limits: null, cancel_withdraw_request: null, loss_limits: null, deposit_limits: null };
			this.auth_token = null;
			this.password_visibility = false;
		},
		getSlotsPlayerStats() {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/games/stats').then((response) => {

				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		getLiveCasinoPlayerStats() {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/tables/stats').then((response) => {

				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		fetchProtectionConfiguration() {
			if (_.isEmpty(this.customer)) return;
			if (this.customer_protection) return Promise.resolve(this.customer_protection);

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/protection-configuration').then((response) => {
				this.customer_protection = response.data.data;
				return Promise.resolve(response.data.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		fetchProtectionLimits() {
			// Do not attempt to fetch if not logged in
			if (_.isEmpty(this.customer)) return;

			return global.axios.get(process.env.VUE_APP_API_URL + '/account/protection-limits').then((response) => {
				this.protection_limits.login_limit = response.data.login_limit ?? null;
				this.protection_limits.time_out = response.data.time_out ?? null;
				this.protection_limits.reality_check = response.data.reality_check ?? null;
				this.protection_limits.time_limits = response.data.time_limits ?? null;
				this.protection_limits.bet_limits = response.data.bet_limits ?? null;
				this.protection_limits.cancel_withdraw_request = response.data.cancel_withdraw_request ?? null;
				this.protection_limits.loss_limits = response.data.loss_limits ?? null;
				this.protection_limits.deposit_limits = response.data.deposit_limits ?? null;

				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},

		// Player Protections - Login Limit
		updateLoginLimit(limit_id) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/login/limits/update', { id: limit_id }).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Deposit Limit
		updateDepositLimits(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/deposit-limits/update', payload).then((response) => {
				return Promise.resolve(response);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Loss Limit
		updateLossLimits(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/loss-limits/update', payload).then((response) => {
				return Promise.resolve(response);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Bet Limits
		updateBetLimits(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/bet-limits/update', payload).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Reality Check
		updateRealityCheck(reality_check_id) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/reality-check/update', { id: reality_check_id }).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Self Exclusion
		updateCustomerProtectionSelfExclusion(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/self-exclusion/update', payload).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Cancel Withdraw Request
		updateCustomerProtectionCancelWithdrawRequest(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/cancel-withdraw-request/update', payload).then((response) => {
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Time Out
		updateCustomerProtectionTimeOut(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/timeout/update', payload).then((response) => {
				return Promise.resolve(response);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Time Limits
		updateCustomerProtectionTimeLimits(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/time-limits/update', payload).then((response) => {
				return Promise.resolve(response);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Player Protections - Set required limits
		updateCustomerDefaultLimits(payload) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/customer-protection/default-limits/update', payload).then((response) => {
				this.fetchProtectionLimits();

				return Promise.resolve(response);
			}).catch((error) => {
				this.protection_limits_errors = error.response.data;

				return Promise.reject(error.response.data);
			})
		},
		updateFavourites(data) {
			this.customer.favourites = data;
		},
		setCustomer(data) {
			if (this.customer === null) {
				this.customer = {};
			}
			for (const key in data) {
				this.customer[key] = data[key];
			}
		},
		dismissNotification(notification_id) {
			if (!this.dismissed_platform_notifications.includes(notification_id)) {
				this.dismissed_platform_notifications.push(notification_id);
			}
		},
		updateCustomerTimezone(timezone_offset) {
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/timezone/update?new=true', { timezone_offset: timezone_offset }).then((response) => {
				this.customer.timezone = response.data.data.timezone;
				return Promise.resolve(response.data);
			}).catch((error) => {
				return Promise.reject(error.response);
			})
		},
		// Mark dropdown notifications as read
		markAsReadDropdownNotifications() {
			const unread_notification_ids = this.getCustomerPopUpNotifications.filter(notification => notification.read_at === null).map(notification => notification.id);

			if (unread_notification_ids.length > 0) {
				return global.axios.post(process.env.VUE_APP_API_URL + '/account/notifications/dropdown/read', { notification_ids: unread_notification_ids }).then((response) => {

					return Promise.resolve(response);
				}).catch((error) => {

					return Promise.reject(error.response.data);
				})
			} else {
				return Promise.resolve();
			}
		},
		fetchCustomerDropdownNotifications() {
			if (!this.customer && !this.isLoggedIn) return;
			return global.axios.post(process.env.VUE_APP_API_URL + '/account/notifications/customer/dropdown').then((response) => {
				this.customer_notifications = response.data.notifications;
				this.customer.unread_notifications_count = response.data.unread_notifications_count;

				return Promise.resolve(response);
			}).catch((error) => {

				return Promise.reject(error);
			});
		},
		setCustomerBalance(payload) {
			this.customer.balance = payload;
		}
	},
	persist: {
		storage: localStorage,
		paths: ['auth_token', 'redirect_after_login', 'dismissed_platform_notifications'],
	},
})